import * as XLSX from "xlsx";
import {saveWorkbook} from "../../../utils/excel";
import {isoToDateString} from "../model/measurementDate";
import {REGION_OPTIONS} from "../../../utils/constants";

const REGIONS = REGION_OPTIONS.map(r => r.value).filter(r => r !== '不限').map(r => `${r}`)

const calculateRegionCount = (data) => {
  // 先把所有地區初始化為 0
  const result = REGIONS.reduce((acc, region) => {
    acc[region] = 0;
    return acc;
  }, {});

  // 再累加實際資料
  data.forEach(item => {
    if (item.region && result.hasOwnProperty(item.region)) {
      result[item.region] += 1;
    }
  });

  return result;
};

function getCalendarWeekOfMonth(date) {
  const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);

  const dayOfMonth = date.getDate();          // 當月第幾天
  const firstDayWeekday = firstDayOfMonth.getDay(); // 0(日)~6(六)

  // 日曆週計算公式
  return Math.ceil((dayOfMonth + firstDayWeekday) / 7);
}

function countByCalendarWeeks(data) {
  const counts = {
    week1: 0,
    week2: 0,
    week3: 0,
    week4: 0
  };

  data.forEach(item => {
    const d = new Date(item.measureDateTime);
    const week = getCalendarWeekOfMonth(d);

    if (week === 1) counts.week1 += 1;
    else if (week === 2) counts.week2 += 1;
    else if (week === 3) counts.week3 += 1;
    else if (week === 4) counts.week3 += 1;
  });

  return counts;
}

function getCurrentMonth(data) {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0-based (0 = Jan)

  return data.filter(item => {
    const d = new Date(item.measureDateTime);
    return (
      d.getFullYear() === currentYear &&
      d.getMonth() === currentMonth
    );
  })
}

function getLastMonth(data) {
  const now = new Date();

  // 計算上個月（自動處理跨年）
  const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastYear = lastMonthDate.getFullYear();
  const lastMonth = lastMonthDate.getMonth(); // 0-based

  return data.filter(item => {
    const d = new Date(item.measureDateTime);
    return (
      d.getFullYear() === lastYear &&
      d.getMonth() === lastMonth
    );
  });
}

function countNewLineIds(currentMonthData, lastMonthData) {
  const currentSet = new Set(currentMonthData.map(item => item.lineId));
  const lastSet = new Set(lastMonthData.map(item => item.lineId));

  let count = 0;
  currentSet.forEach(lineId => {
    if (!lastSet.has(lineId)) {
      count += 1;
    }
  });

  return count;
}

export const useExportMeasurement = ({
  measurements,
  guests,
}) => {
  // export cases detail to excel file
  const exportData = () => {
    const workbook = XLSX.utils.book_new();
    const guestMapping = {}
    for(const g of guests) {
      guestMapping[g.lineUserId] = g
    }

    for (let i=0; i<measurements.length; i++) {
      measurements[i].name = guestMapping[measurements[i].lineId].name
      measurements[i].region = guestMapping[measurements[i].lineId].region
    }

    // =====================================================================
    // Sheet 1: 血壓
    // =====================================================================
    const valuesSheet1 = [['使用者名稱', '紀錄時間', '收縮壓', '舒張壓']]
    const filteredMeasurements1 = measurements.filter(measurement => measurement.type === 'bp')

    for(const measurement of filteredMeasurements1) {
      valuesSheet1.push([
        measurement.name,
        isoToDateString(measurement.measureDateTime),
        measurement.measurement.systolic,
        measurement.measurement.diastolic,
      ])
    }
    const worksheet1 = XLSX.utils.aoa_to_sheet(valuesSheet1);

    // =====================================================================
    // Sheet 2: 血糖
    // =====================================================================
    const valuesSheet2 = [['使用者名稱', '紀錄時間', '血糖(mg/dl)']]
    const filteredMeasurements2 = measurements.filter(measurement => measurement.type === 'glucose')

    for(const measurement of filteredMeasurements2) {
      valuesSheet2.push([
        measurement.name,
        isoToDateString(measurement.measureDateTime),
        measurement.measurement.glucose,
      ])
    }
    const worksheet2 = XLSX.utils.aoa_to_sheet(valuesSheet2);

    // =====================================================================
    // Sheet 3: 體溫
    // =====================================================================
    const valuesSheet3 = [['使用者名稱', '紀錄時間', '體溫']]
    const filteredMeasurements3 = measurements.filter(measurement => measurement.type === 'temp')

    for(const measurement of filteredMeasurements3) {
      valuesSheet3.push([
        measurement.name,
        isoToDateString(measurement.measureDateTime),
        measurement.measurement.bodyTemperature,
      ])
    }
    const worksheet3 = XLSX.utils.aoa_to_sheet(valuesSheet3);

    // =====================================================================
    // Sheet 4: 心跳
    // =====================================================================
    const valuesSheet4 = [['使用者名稱', '紀錄時間', '心跳(bpm)']]
    const filteredMeasurements4 = measurements.filter(measurement => measurement.type === 'pulse')

    for(const measurement of filteredMeasurements4) {
      valuesSheet4.push([
        measurement.name,
        isoToDateString(measurement.measureDateTime),
        measurement.measurement.pulse,
      ])
    }
    const worksheet4 = XLSX.utils.aoa_to_sheet(valuesSheet4);

    // =====================================================================
    // Sheet 5: 血氧
    // =====================================================================
    const valuesSheet5 = [['使用者名稱', '紀錄時間', '血氧(%)']]
    const filteredMeasurements5 = measurements.filter(measurement => measurement.type === 'oxygen')

    for(const measurement of filteredMeasurements5) {
      valuesSheet5.push([
        measurement.name,
        isoToDateString(measurement.measureDateTime),
        measurement.measurement.bloodOxygen
      ])
    }
    const worksheet5 = XLSX.utils.aoa_to_sheet(valuesSheet5);

    // =====================================================================
    // Useful Metrics
    // =====================================================================
    const maleGuests =  guests.filter(g => g.gender === '男')
    const femaleGuests =  guests.filter(g => g.gender === '女')
    const indigenousGuests =  guests.filter(g => g.isIndigenous === true)
    const currentMonthMeasurements = getCurrentMonth(measurements)
    const lastMonthMeasurements = getLastMonth(measurements)
    const allTimeBpMeasurements = measurements.filter(measurement => measurement.type === 'bp')
    const allTimeGlucoseMeasurements = measurements.filter(measurement => measurement.type === 'glucose')
    const currentMonthBpMeasurements = currentMonthMeasurements.filter(measurement => measurement.type === 'bp')
    const currentMonthGlucoseMeasurements = currentMonthMeasurements.filter(measurement => measurement.type === 'glucose')
    const calendarWeekCurrentMonthBpMeasurements = countByCalendarWeeks(currentMonthBpMeasurements)
    const calendarWeekCurrentMonthGlucoseMeasurements = countByCalendarWeeks(currentMonthGlucoseMeasurements)

    // =====================================================================
    // Sheet 6: 統計
    // =====================================================================
    const valuesSheet6 = [['總填寫人數', '本月新增填寫人數', '男生填寫人數', '女生填寫人數', '原住民填寫人數']]
    valuesSheet6.push([
      guests.length,
      countNewLineIds(currentMonthMeasurements, lastMonthMeasurements),
      maleGuests.length,
      femaleGuests.length,
      indigenousGuests.length,
    ])
    valuesSheet6.push([])
    valuesSheet6.push([])
    valuesSheet6.push(['各鄉鎮市填寫總數'])
    valuesSheet6.push([guests.length])

    const regionCount = (calculateRegionCount(measurements))
    valuesSheet6.push(Object.keys(regionCount).map(label => `${label}填寫數`))
    valuesSheet6.push(Object.values(regionCount))

    valuesSheet6.push([])
    valuesSheet6.push([])
    valuesSheet6.push(['血壓填寫總數', '第一周血壓填寫總數', '第二周血壓填寫總數', '第三周血壓填寫總數', '第四周血壓填寫總數', '本月血壓填寫總數'])
    valuesSheet6.push([
      allTimeBpMeasurements.length,
      calendarWeekCurrentMonthBpMeasurements.week1,
      calendarWeekCurrentMonthBpMeasurements.week2,
      calendarWeekCurrentMonthBpMeasurements.week3,
      calendarWeekCurrentMonthBpMeasurements.week4,
      currentMonthBpMeasurements.length,
    ])

    valuesSheet6.push([])
    valuesSheet6.push(['血糖填寫總數', '第一周血糖填寫總數', '第二周血糖填寫總數', '第三周血糖填寫總數', '第四周血糖填寫總數', '本月血糖填寫總數'])
    valuesSheet6.push([
      allTimeGlucoseMeasurements.length,
      calendarWeekCurrentMonthGlucoseMeasurements.week1,
      calendarWeekCurrentMonthGlucoseMeasurements.week2,
      calendarWeekCurrentMonthGlucoseMeasurements.week3,
      calendarWeekCurrentMonthGlucoseMeasurements.week4,
      currentMonthGlucoseMeasurements.length,
    ])
    const worksheet6 = XLSX.utils.aoa_to_sheet(valuesSheet6);


    // =====================================================================
    // Sheet 7: 分析
    // =====================================================================
    const valuesSheet7 = [['性別', '', '其他', '']]
    valuesSheet7.push(['男性填寫比率', '女性填寫比率', '原住民使用比率', '每月血壓填寫比率', '每月血糖填寫比率'])
    valuesSheet7.push([
      (maleGuests.length / guests.length * 100).toFixed(2),
      (femaleGuests.length / guests.length * 100).toFixed(2),
      (indigenousGuests.length / guests.length * 100).toFixed(2),
      (currentMonthBpMeasurements.length / measurements.filter(m => m.type === 'bp').length * 100).toFixed(2),
      (currentMonthGlucoseMeasurements.length / measurements.filter(m => m.type === 'glucose').length * 100).toFixed(2),
    ])

    valuesSheet7.push([])
    valuesSheet7.push([])
    valuesSheet7.push([])
    valuesSheet7.push([])
    valuesSheet7.push([])
    valuesSheet7.push(['縣市鄉鎮市使用比率'])
    valuesSheet7.push([])
    valuesSheet7.push(Object.keys(regionCount).map(label => `${label}使用比率`))
    valuesSheet7.push(Object.values(regionCount).map((c: number) => (c / measurements.length * 100).toFixed(2)))

    const worksheet7 = XLSX.utils.aoa_to_sheet(valuesSheet7);

    XLSX.utils.book_append_sheet(workbook, worksheet1, '血壓');
    XLSX.utils.book_append_sheet(workbook, worksheet2, '血糖');
    XLSX.utils.book_append_sheet(workbook, worksheet3, '體溫');
    XLSX.utils.book_append_sheet(workbook, worksheet4, '脈搏');
    XLSX.utils.book_append_sheet(workbook, worksheet5, '血氧');
    XLSX.utils.book_append_sheet(workbook, worksheet6, '資料統計');
    XLSX.utils.book_append_sheet(workbook, worksheet7, '資料分析');
    saveWorkbook({ workbook, fileName: '生理量測數據報表'})
  }
  return { exportData }
}
